/**
 * Setup Phases Modal
 * Allows users to configure phases for a site with drag-and-drop reordering
 *
 * The "Site Progress %" represents cumulative progress when that phase is complete:
 * - Phase 1 at 10% means site is 10% done when Phase 1 completes
 * - Phase 2 at 25% means site is 25% done when Phase 2 completes
 * - Final phase should be at 100%
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ResponsiveModal from '../common/ResponsiveModal';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { sitePhaseTemplateAPI } from '../../services/api';

// Phase templates with CUMULATIVE progress percentages
// Each value represents site progress when that phase is complete
const RENOVATION_PHASES = [
  { phaseKey: 'handover', name: 'Handover', siteProgress: 5 },
  { phaseKey: 'demolition', name: 'Demolition', siteProgress: 15 },
  { phaseKey: 'me_1st_fix', name: 'M&E 1st Fix', siteProgress: 25 },
  { phaseKey: 'wet_works', name: 'Wet Works', siteProgress: 40 },
  { phaseKey: 'ceiling_partition', name: 'Ceiling/Partition', siteProgress: 50 },
  { phaseKey: 'carpentry', name: 'Carpentry', siteProgress: 70 },
  { phaseKey: 'painting', name: 'Painting', siteProgress: 80 },
  { phaseKey: 'me_2nd_fix', name: 'M&E 2nd Fix', siteProgress: 90 },
  { phaseKey: 'defects_qc', name: 'Defects & QC', siteProgress: 95 },
  { phaseKey: 'client_handover', name: 'Client Handover', siteProgress: 100 }
];

const BUILD_FROM_LAND_PHASES = [
  { phaseKey: 'site_handover', name: 'Site Handover', siteProgress: 3 },
  { phaseKey: 'foundation', name: 'Foundation', siteProgress: 15 },
  { phaseKey: 'structure', name: 'Structure/Frame', siteProgress: 30 },
  { phaseKey: 'roofing', name: 'Roofing', siteProgress: 38 },
  { phaseKey: 'me_1st_fix', name: 'M&E 1st Fix', siteProgress: 46 },
  { phaseKey: 'external_walls', name: 'External Walls', siteProgress: 56 },
  { phaseKey: 'windows_doors', name: 'Windows & Doors', siteProgress: 61 },
  { phaseKey: 'internal_walls', name: 'Internal Walls', siteProgress: 66 },
  { phaseKey: 'wet_works', name: 'Wet Works', siteProgress: 74 },
  { phaseKey: 'ceiling', name: 'Ceiling', siteProgress: 78 },
  { phaseKey: 'carpentry', name: 'Carpentry', siteProgress: 86 },
  { phaseKey: 'painting', name: 'Painting', siteProgress: 91 },
  { phaseKey: 'me_2nd_fix', name: 'M&E 2nd Fix', siteProgress: 95 },
  { phaseKey: 'external_works', name: 'External Works', siteProgress: 98 },
  { phaseKey: 'defects_qc', name: 'Defects & QC', siteProgress: 100 }
];

// Sortable Phase Item Component
const SortablePhaseItem = ({ phase, index, totalPhases, onUpdate, onRemove, prevProgress }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Validate progress is greater than previous phase
  const isProgressValid = phase.siteProgress > (prevProgress || 0);
  const isLastPhase = index === totalPhases - 1;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-white border rounded-lg ${
        isDragging ? 'shadow-lg border-blue-400' :
        !isProgressValid ? 'border-red-300 bg-red-50' : 'border-gray-200'
      }`}
    >
      {/* Top row on mobile: drag + sequence + name */}
      <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-2 sm:p-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center touch-manipulation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </button>

        {/* Sequence Number */}
        <span className="w-8 h-8 sm:w-6 sm:h-6 flex items-center justify-center bg-gray-100 rounded-full text-sm sm:text-xs font-medium text-gray-600 flex-shrink-0">
          {phase.sequence}
        </span>

        {/* Phase Name Input */}
        <input
          type="text"
          value={phase.name}
          onChange={(e) => onUpdate(phase.id, { name: e.target.value })}
          className="flex-1 px-3 sm:px-2 py-2 sm:py-1 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] sm:min-h-0"
          placeholder="Phase name"
        />
      </div>

      {/* Bottom row on mobile: progress + remove */}
      <div className="flex items-center justify-between sm:justify-end gap-2 pl-14 sm:pl-0">
        {/* Site Progress Input */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">Site:</span>
          <input
            type="number"
            value={phase.siteProgress}
            onChange={(e) => onUpdate(phase.id, { siteProgress: parseFloat(e.target.value) || 0 })}
            min={1}
            max={100}
            className={`w-20 sm:w-16 px-2 py-2 sm:py-1 text-sm text-center border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] sm:min-h-0 ${
              !isProgressValid ? 'border-red-400 bg-red-50' : 'border-gray-200'
            }`}
          />
          <span className="text-sm text-gray-500">%</span>
          {/* Last phase indicator */}
          {isLastPhase && phase.siteProgress !== 100 && (
            <span className="text-xs text-orange-500" title="Last phase should be 100%">!</span>
          )}
        </div>

        {/* Remove Button */}
        <button
          onClick={() => onRemove(phase.id)}
          className="p-2 sm:p-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
          title="Remove phase"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const SetupPhasesModal = ({
  isOpen,
  onClose,
  site,
  projectType = 'renovation',
  existingPhases = [],
  onConfirm,
  loading = false
}) => {
  const [selectedType, setSelectedType] = useState(site?.projectType || projectType);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [customPhaseName, setCustomPhaseName] = useState('');
  const [customPhaseProgress, setCustomPhaseProgress] = useState(50);

  // Saved templates state
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Save template modal state
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateDescription, setSaveTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Manage templates modal state
  const [showManageTemplatesModal, setShowManageTemplatesModal] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);

  // Mobile detection for responsive behavior
  const isMobile = useMediaQuery('(max-width: 639px)');

  // DnD sensors - with touch support for mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get template based on project type
  const getTemplatePhases = useCallback(() => {
    return selectedType === 'build_from_land' ? BUILD_FROM_LAND_PHASES : RENOVATION_PHASES;
  }, [selectedType]);

  // Fetch saved templates
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = await sitePhaseTemplateAPI.list();
      if (response.data.success) {
        setSavedTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  // Load saved templates on modal open
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, fetchTemplates]);

  // Load template into selected phases
  const loadTemplate = (templateId) => {
    const template = savedTemplates.find(t => t.id === templateId);
    if (!template) return;

    // Update project type if different
    if (template.projectType !== selectedType) {
      setSelectedType(template.projectType);
    }

    // Load phases from template
    const loadedPhases = template.phases.map((phase, index) => ({
      id: `loaded_${phase.phaseKey}_${Date.now()}_${index}`,
      phaseKey: phase.phaseKey,
      name: phase.name,
      siteProgress: phase.siteProgress,
      sequence: index + 1
    }));

    setSelectedPhases(loadedPhases);
    setSelectedTemplateId('');
  };

  // Save current phases as template
  const handleSaveTemplate = async () => {
    if (!saveTemplateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (selectedPhases.length === 0) {
      alert('Please add at least one phase before saving');
      return;
    }

    const lastPhase = selectedPhases[selectedPhases.length - 1];
    if (lastPhase.siteProgress !== 100) {
      alert('Last phase must be at 100% to save as template');
      return;
    }

    setSavingTemplate(true);
    try {
      const templateData = {
        name: saveTemplateName.trim(),
        description: saveTemplateDescription.trim() || null,
        projectType: selectedType,
        phases: selectedPhases.map(p => ({
          phaseKey: p.phaseKey,
          name: p.name,
          siteProgress: p.siteProgress
        }))
      };

      const response = await sitePhaseTemplateAPI.create(templateData);
      if (response.data.success) {
        // Refresh templates list
        await fetchTemplates();
        // Close save modal and reset
        setShowSaveTemplateModal(false);
        setSaveTemplateName('');
        setSaveTemplateDescription('');
        alert('Template saved successfully!');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert(error.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    setDeletingTemplateId(templateId);
    try {
      const response = await sitePhaseTemplateAPI.delete(templateId);
      if (response.data.success) {
        await fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(error.message || 'Failed to delete template');
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // Convert weights to cumulative progress for existing phases
  const convertWeightsToProgress = (phases) => {
    let cumulative = 0;
    return phases.map((p, index) => {
      cumulative += (p.weight || 10);
      return {
        id: p.id || `existing_${index}`,
        phaseKey: p.phaseKey,
        name: p.name,
        siteProgress: Math.min(100, cumulative),
        sequence: index + 1
      };
    });
  };

  // Get default phases for first-time setup
  const getDefaultPhases = useCallback((type) => {
    if (type === 'build_from_land') {
      return [
        { id: `default_site_handover_${Date.now()}`, phaseKey: 'site_handover', name: 'Site Handover', siteProgress: 5, sequence: 1 },
        { id: `default_defects_qc_${Date.now()}`, phaseKey: 'defects_qc', name: 'Defects & QC', siteProgress: 100, sequence: 2 }
      ];
    }
    return [
      { id: `default_handover_${Date.now()}`, phaseKey: 'handover', name: 'Handover', siteProgress: 5, sequence: 1 },
      { id: `default_client_handover_${Date.now()}`, phaseKey: 'client_handover', name: 'Client Handover', siteProgress: 100, sequence: 2 }
    ];
  }, []);

  // Initialize with existing phases or defaults
  useEffect(() => {
    if (isOpen) {
      const type = site?.projectType || projectType;
      if (existingPhases.length > 0) {
        setSelectedPhases(convertWeightsToProgress(existingPhases));
      } else {
        // First-time setup: default to Handover (5%) and Client Handover (100%)
        setSelectedPhases(getDefaultPhases(type));
      }
      setCustomPhaseName('');
      setCustomPhaseProgress(50);
      if (site?.projectType) {
        setSelectedType(site.projectType);
      }
    }
  }, [isOpen, existingPhases, site?.projectType, projectType, getDefaultPhases]);

  // Validate phases - check ascending order
  const validatePhases = () => {
    if (selectedPhases.length === 0) return { valid: false, message: 'Add at least one phase' };

    let prevProgress = 0;
    for (let i = 0; i < selectedPhases.length; i++) {
      if (selectedPhases[i].siteProgress <= prevProgress) {
        return { valid: false, message: `Phase ${i + 1} progress must be greater than ${prevProgress}%` };
      }
      prevProgress = selectedPhases[i].siteProgress;
    }

    const lastPhase = selectedPhases[selectedPhases.length - 1];
    if (lastPhase.siteProgress !== 100) {
      return { valid: false, message: 'Last phase should reach 100%' };
    }

    return { valid: true };
  };

  const validation = validatePhases();

  // Check if a template phase is selected
  const isPhaseSelected = (phaseKey) => {
    return selectedPhases.some(p => p.phaseKey === phaseKey);
  };

  // Toggle a template phase
  const toggleTemplatePhase = (templatePhase) => {
    if (isPhaseSelected(templatePhase.phaseKey)) {
      // Remove phase
      setSelectedPhases(prev => {
        const filtered = prev.filter(p => p.phaseKey !== templatePhase.phaseKey);
        return filtered.map((p, i) => ({ ...p, sequence: i + 1 }));
      });
    } else {
      // Add phase at the end
      setSelectedPhases(prev => [
        ...prev,
        {
          id: `template_${templatePhase.phaseKey}_${Date.now()}`,
          phaseKey: templatePhase.phaseKey,
          name: templatePhase.name,
          siteProgress: templatePhase.siteProgress,
          sequence: prev.length + 1
        }
      ]);
    }
  };

  // Add all template phases
  const addAllTemplatePhases = () => {
    const template = getTemplatePhases();
    setSelectedPhases(
      template.map((p, index) => ({
        id: `template_${p.phaseKey}_${Date.now()}_${index}`,
        phaseKey: p.phaseKey,
        name: p.name,
        siteProgress: p.siteProgress,
        sequence: index + 1
      }))
    );
  };

  // Clear all phases
  const clearAllPhases = () => {
    setSelectedPhases([]);
  };

  // Add custom phase
  const addCustomPhase = () => {
    if (!customPhaseName.trim()) return;

    setSelectedPhases(prev => [
      ...prev,
      {
        id: `custom_${Date.now()}`,
        phaseKey: `custom_${Date.now()}`,
        name: customPhaseName.trim(),
        siteProgress: customPhaseProgress,
        sequence: prev.length + 1
      }
    ]);
    setCustomPhaseName('');
    setCustomPhaseProgress(50);
  };

  // Update a phase
  const updatePhase = (id, updates) => {
    setSelectedPhases(prev =>
      prev.map(p => (p.id === id ? { ...p, ...updates } : p))
    );
  };

  // Remove a phase
  const removePhase = (id) => {
    setSelectedPhases(prev => {
      const filtered = prev.filter(p => p.id !== id);
      return filtered.map((p, i) => ({ ...p, sequence: i + 1 }));
    });
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSelectedPhases((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((p, i) => ({ ...p, sequence: i + 1 }));
      });
    }
  };

  // Convert cumulative progress to weights for backend
  const convertProgressToWeights = (phases) => {
    let prevProgress = 0;
    return phases.map((phase, index) => {
      const weight = phase.siteProgress - prevProgress;
      prevProgress = phase.siteProgress;
      return {
        phaseKey: phase.phaseKey,
        name: phase.name,
        sequence: index + 1,
        completionPercent: weight // This will be stored as "weight" in backend
      };
    });
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    // Convert to weights for backend
    const phasesWithWeights = convertProgressToWeights(selectedPhases);
    onConfirm && onConfirm(phasesWithWeights);
  };

  return (
    <>
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Setup Phases - ${site?.projectNumber || ''}`}
      size="lg"
      maxHeight="90vh"
    >
      <div className={`space-y-6 ${isMobile ? 'px-4 pb-6' : 'max-h-[70vh] overflow-y-auto'}`}>
        {/* Pending SS Assignment Warning */}
        {site?.siteStatus === 'pending_ss_assignment' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> You can configure phases now, but the site cannot start until a Site Supervisor is assigned.
            </p>
          </div>
        )}

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            <strong>Site Progress %</strong> = The overall site completion when this phase is done.
            <br />
            <span className="text-blue-600">Example: Phase at 25% means site is 25% complete when that phase finishes.</span>
          </p>
        </div>

        {/* Project Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Project Type</label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input
                type="radio"
                name="projectType"
                value="renovation"
                checked={selectedType === 'renovation'}
                onChange={() => setSelectedType('renovation')}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Renovation</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer min-h-[44px]">
              <input
                type="radio"
                name="projectType"
                value="build_from_land"
                checked={selectedType === 'build_from_land'}
                onChange={() => setSelectedType('build_from_land')}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Build from Land</span>
            </label>
          </div>
        </div>

        {/* My Saved Templates */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <label className="text-sm font-medium text-gray-700">My Saved Templates</label>
            {savedTemplates.length > 0 && (
              <button
                type="button"
                onClick={() => setShowManageTemplatesModal(true)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Manage Templates
              </button>
            )}
          </div>
          {templatesLoading ? (
            <div className="text-sm text-gray-500">Loading templates...</div>
          ) : savedTemplates.length === 0 ? (
            <div className="text-sm text-gray-500">No saved templates yet. Create one by configuring phases and clicking "Save as Template".</div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] sm:min-h-0"
              >
                <option value="">Select a template...</option>
                {savedTemplates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.phases.length} phases, {template.projectType === 'build_from_land' ? 'Build' : 'Renovation'})
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => selectedTemplateId && loadTemplate(selectedTemplateId)}
                disabled={!selectedTemplateId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0 w-full sm:w-auto"
              >
                Load Template
              </button>
            </div>
          )}
        </div>

        {/* Template Phases */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">Template Phases</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={addAllTemplatePhases}
                className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 min-h-[32px] touch-manipulation"
              >
                Add All
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={clearAllPhases}
                className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 min-h-[32px] touch-manipulation"
              >
                Clear All
              </button>
            </div>
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 p-2 bg-gray-50 rounded-lg ${
            isMobile ? '' : 'max-h-60 sm:max-h-48 overflow-y-auto'
          }`}>
            {getTemplatePhases().map(phase => (
              <label
                key={phase.phaseKey}
                className={`flex items-center gap-2 p-3 sm:p-2 rounded cursor-pointer transition-colors min-h-[48px] sm:min-h-0 touch-manipulation ${
                  isPhaseSelected(phase.phaseKey)
                    ? 'bg-blue-100 border border-blue-300'
                    : 'bg-white border border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isPhaseSelected(phase.phaseKey)}
                  onChange={() => toggleTemplatePhase(phase)}
                  className="w-5 h-5 sm:w-4 sm:h-4 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-sm flex-1">{phase.name}</span>
                <span className="text-xs text-gray-500">{phase.siteProgress}%</span>
              </label>
            ))}
          </div>
        </div>

        {/* Add Custom Phase */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Add Custom Phase</label>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <input
              type="text"
              value={customPhaseName}
              onChange={(e) => setCustomPhaseName(e.target.value)}
              placeholder="Phase name"
              className="w-full sm:flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              onKeyDown={(e) => e.key === 'Enter' && addCustomPhase()}
            />
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 flex-1 sm:flex-none">
                <span className="text-xs text-gray-400">Site:</span>
                <input
                  type="number"
                  value={customPhaseProgress}
                  onChange={(e) => setCustomPhaseProgress(parseFloat(e.target.value) || 0)}
                  min={1}
                  max={100}
                  className="w-20 sm:w-16 px-2 py-2 text-sm text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] sm:min-h-0"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
              <button
                type="button"
                onClick={addCustomPhase}
                disabled={!customPhaseName.trim()}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Selected Phases (Drag to Reorder) */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <label className="text-sm font-medium text-gray-700">
                Selected Phases ({selectedPhases.length})
              </label>
              {selectedPhases.length > 0 && validation.valid && (
                <button
                  type="button"
                  onClick={() => setShowSaveTemplateModal(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 touch-manipulation"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save as Template
                </button>
              )}
            </div>
            <span className="text-xs text-gray-500 hidden sm:block">Drag to reorder • Progress must increase</span>
            <span className="text-xs text-gray-500 sm:hidden">Touch & hold to drag • Progress must increase</span>
          </div>

          {selectedPhases.length === 0 ? (
            <div className="p-8 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No phases selected</p>
              <p className="text-xs text-gray-400">Select from templates or add custom phases above</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selectedPhases.map(p => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={`space-y-2 ${isMobile ? '' : 'max-h-64 overflow-y-auto'}`}>
                  {selectedPhases.map((phase, index) => (
                    <SortablePhaseItem
                      key={phase.id}
                      phase={phase}
                      index={index}
                      totalPhases={selectedPhases.length}
                      prevProgress={index > 0 ? selectedPhases[index - 1].siteProgress : 0}
                      onUpdate={updatePhase}
                      onRemove={removePhase}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Validation Status */}
        {selectedPhases.length > 0 && (
          <div className={`p-3 rounded-lg ${
            validation.valid
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {validation.valid ? (
                <>
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-green-800">
                    Valid setup: {selectedPhases.length} phases, ends at 100%
                  </span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-red-800">{validation.message}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={`mt-6 flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t ${
        isMobile ? 'sticky bottom-0 bg-white pb-safe' : ''
      }`}>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 min-h-[48px] sm:min-h-0"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading || !validation.valid}
          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] sm:min-h-0"
        >
          {loading ? 'Setting up...' : 'Confirm Setup'}
        </button>
      </div>
    </ResponsiveModal>

    {/* Save Template Modal */}
    <ResponsiveModal
      isOpen={showSaveTemplateModal}
      onClose={() => {
        setShowSaveTemplateModal(false);
        setSaveTemplateName('');
        setSaveTemplateDescription('');
      }}
      title="Save as Template"
      size="sm"
      maxHeight="60vh"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
          <input
            type="text"
            value={saveTemplateName}
            onChange={(e) => setSaveTemplateName(e.target.value)}
            placeholder="e.g., My Custom Renovation"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
          <textarea
            value={saveTemplateDescription}
            onChange={(e) => setSaveTemplateDescription(e.target.value)}
            placeholder="Brief description of this template..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-600 mb-2">Template will include:</p>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>• {selectedPhases.length} phases</li>
            <li>• Project type: {selectedType === 'build_from_land' ? 'Build from Land' : 'Renovation'}</li>
            <li>• Progress from 0% to 100%</li>
          </ul>
        </div>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => {
              setShowSaveTemplateModal(false);
              setSaveTemplateName('');
              setSaveTemplateDescription('');
            }}
            disabled={savingTemplate}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveTemplate}
            disabled={savingTemplate || !saveTemplateName.trim()}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
          >
            {savingTemplate ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>
    </ResponsiveModal>

    {/* Manage Templates Modal */}
    <ResponsiveModal
      isOpen={showManageTemplatesModal}
      onClose={() => setShowManageTemplatesModal(false)}
      title="Manage Templates"
      size="md"
      maxHeight="75vh"
    >
      <div className={`space-y-4 ${isMobile ? 'px-4 pb-4' : ''}`}>
        {savedTemplates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <svg className="mx-auto h-10 w-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm">No templates saved yet</p>
          </div>
        ) : (
          <div className={`space-y-3 ${isMobile ? '' : 'max-h-[50vh] overflow-y-auto'}`}>
            {savedTemplates.filter(t => !t.isGlobal).map(template => (
              <div
                key={template.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">{template.name}</h4>
                  {template.description && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{template.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">
                      {template.phases.length} phases
                    </span>
                    <span className="text-xs px-1.5 py-0.5 bg-gray-200 rounded">
                      {template.projectType === 'build_from_land' ? 'Build' : 'Renovation'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      loadTemplate(template.id);
                      setShowManageTemplatesModal(false);
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 min-h-[36px] touch-manipulation"
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteTemplate(template.id)}
                    disabled={deletingTemplateId === template.id}
                    className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50 min-h-[36px] touch-manipulation"
                  >
                    {deletingTemplateId === template.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
            {/* Show global templates separately if any */}
            {savedTemplates.filter(t => t.isGlobal).length > 0 && (
              <>
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-2">System Templates (Read-only)</p>
                </div>
                {savedTemplates.filter(t => t.isGlobal).map(template => (
                  <div
                    key={template.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-3 bg-blue-50 rounded-lg border border-blue-200"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">{template.name}</h4>
                      {template.description && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{template.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {template.phases.length} phases
                        </span>
                        <span className="text-xs px-1.5 py-0.5 bg-blue-100 rounded">
                          {template.projectType === 'build_from_land' ? 'Build' : 'Renovation'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        loadTemplate(template.id);
                        setShowManageTemplatesModal(false);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-100 min-h-[36px] touch-manipulation"
                    >
                      Load
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        <div className="flex justify-end pt-2 border-t">
          <button
            type="button"
            onClick={() => setShowManageTemplatesModal(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] sm:min-h-0"
          >
            Close
          </button>
        </div>
      </div>
    </ResponsiveModal>
  </>
  );
};

export default SetupPhasesModal;
